/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { ResponsiveTable, useLocalization } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { useTrpc } from "@app/vue/providers/index";
import type { CoinEarnRule, CoinProgramWithStats } from "@lemmonhq/types";
import { CoinProgramEditor } from "./CoinProgramEditor";
import { coinIconOf } from "./icons";

type StatTone = "primary" | "success" | "info";

// Spelled out rather than interpolated: Tailwind only emits classes it can find
// as complete strings in the source.
const STAT_TONES: Record<StatTone, { bg: string; text: string }> = {
  primary: { bg: "bg-primary/10", text: "text-primary" },
  success: { bg: "bg-success/10", text: "text-success" },
  info: { bg: "bg-info/10", text: "text-info" },
};

const StatCard = defineComponent<{
  icon: string;
  label: string;
  value: string | number;
  tone: StatTone;
}>({
  name: "CoinProgramStatCard",
  props: ["icon", "label", "value", "tone"],
  setup(props) {
    return () => {
      const tone = STAT_TONES[props.tone];
      return (
        <div class="flex items-center gap-4 bg-base-100 rounded-xl border border-base-300/40 px-5 py-4 l-box-shadow">
          <div
            class={`w-11 h-11 rounded-xl ${tone.bg} flex items-center justify-center shrink-0`}
          >
            <span class={`material-symbols-outlined text-xl ${tone.text}`}>
              {props.icon}
            </span>
          </div>
          <div class="min-w-0">
            <p class="text-xs text-base-content/40 uppercase tracking-wider font-medium">
              {props.label}
            </p>
            <p class={`text-2xl font-bold leading-tight ${tone.text}`}>
              {props.value}
            </p>
          </div>
        </div>
      );
    };
  },
});

export type LoyaltyCoinProgramsPageProps = {
  scope?: "shop" | "group";
  canManage?: boolean;
};

export const LoyaltyCoinProgramsPage =
  defineComponent<LoyaltyCoinProgramsPageProps>({
    name: "LoyaltyCoinProgramsPage",
    props: ["scope", "canManage"],
    setup(props) {
      const { t } = useLocalization();
      const trpc = useTrpc();
      const queryClient = useQueryClient();

      const editingProgram = ref<CoinProgramWithStats | null>(null);
      const showEditor = ref(false);

      const programsQuery = useQuery({
        queryKey: ["coinPrograms", props.scope ?? "shop", "list"],
        queryFn: () =>
          props.scope === "group"
            ? trpc.coinPrograms.groupList.query({ enabled_only: false })
            : trpc.coinPrograms.list.query({ enabled_only: false }),
        staleTime: 30_000,
      });

      const programs = computed<CoinProgramWithStats[]>(
        () => (programsQuery.data?.value ?? []) as CoinProgramWithStats[],
      );

      const toggleMutation = useMutation({
        mutationFn: (vars: { id: string; enabled: boolean }) =>
          props.scope === "group"
            ? trpc.coinPrograms.groupToggleEnabled.mutate(vars)
            : trpc.coinPrograms.toggleEnabled.mutate(vars),
        onSuccess: () =>
          queryClient.invalidateQueries({ queryKey: ["coinPrograms"] }),
      });

      /** One line per program, so the table says what actually mints a coin. */
      const describeRule = (rule: CoinEarnRule, coinLabel: string): string => {
        switch (rule.type) {
          case "spend":
            return t("promotions.coins.summary_spend", [
              rule.coins_per_unit,
              coinLabel,
              rule.unit_amount,
            ]);
          case "product":
            return t("promotions.coins.summary_product", [
              rule.coins,
              coinLabel,
              rule.product_ids.length,
            ]);
          case "category":
            return t("promotions.coins.summary_category", [
              rule.coins,
              coinLabel,
              rule.category_ids.length,
            ]);
          case "visit":
            return t("promotions.coins.summary_visit", [rule.coins, coinLabel]);
          case "referral":
            return t("promotions.coins.summary_referral", [
              rule.referrer_coins,
              coinLabel,
            ]);
        }
      };

      const handleNew = () => {
        editingProgram.value = null;
        showEditor.value = true;
      };

      const handleEdit = (program: CoinProgramWithStats) => {
        editingProgram.value = program;
        showEditor.value = true;
      };

      const closeEditor = () => {
        showEditor.value = false;
        editingProgram.value = null;
      };

      const columns = computed<ResponsiveTableColumn[]>(() => [
        {
          key: "name",
          label: t("promotions.coins.col_program"),
          icon: "toll",
          mobilePriority: "primary",
          render: (row: CoinProgramWithStats) => (
            <div class="flex items-center gap-3 min-w-0">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span class="material-symbols-outlined text-lg text-primary">
                  {coinIconOf(row.icon)}
                </span>
              </div>
              <div class="flex flex-col min-w-0">
                <span class="font-medium text-accent truncate">{row.name}</span>
                <span class="text-xs text-base-content/40 font-mono truncate">
                  {row.coin_type}
                </span>
              </div>
            </div>
          ),
        },
        {
          key: "earning",
          label: t("promotions.coins.col_earning"),
          mobilePriority: "secondary",
          render: (row: CoinProgramWithStats) => {
            // earn_rules is merchant-authored JSONB; a malformed value must not
            // take the whole table down with it.
            const rules = Array.isArray(row.earn_rules) ? row.earn_rules : [];
            return rules.length === 0 ? (
              <span class="text-sm text-warning">
                {t("promotions.coins.col_earning_none")}
              </span>
            ) : (
              <div class="flex flex-col gap-0.5">
                {rules.slice(0, 2).map((rule, i) => (
                  <span key={i} class="text-sm text-base-content/60">
                    {describeRule(rule, row.coin_label)}
                  </span>
                ))}
                {rules.length > 2 && (
                  <span class="text-xs text-base-content/40">
                    {t("promotions.coins.col_earning_more", [rules.length - 2])}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          key: "members",
          label: t("promotions.coins.col_members"),
          mobilePriority: "secondary",
          render: (row: CoinProgramWithStats) => (
            <span class="text-sm text-base-content/70">
              {row.active_members.toLocaleString()}
            </span>
          ),
        },
        {
          key: "outstanding",
          label: t("promotions.coins.col_outstanding"),
          mobilePriority: "secondary",
          render: (row: CoinProgramWithStats) => (
            <span class="text-sm text-base-content/70">
              {row.coins_outstanding.toLocaleString()}
            </span>
          ),
        },
        {
          key: "redeemed",
          label: t("promotions.coins.col_redeemed"),
          mobilePriority: "hidden",
          render: (row: CoinProgramWithStats) => (
            <span class="text-sm text-base-content/70">
              {row.coins_redeemed.toLocaleString()}
            </span>
          ),
        },
        {
          key: "actions",
          label: "",
          mobilePriority: "actions",
          render: (row: CoinProgramWithStats) => (
            <div
              class="flex items-center justify-end gap-2"
              onClick={(e: Event) => e.stopPropagation()}
            >
              {/* Disabling stops earning; outstanding coins stay redeemable. */}
              <input
                type="checkbox"
                checked={row.enabled}
                disabled={
                  toggleMutation.isPending.value || props.canManage === false
                }
                onChange={(e) =>
                  toggleMutation.mutate({
                    id: row.id,
                    enabled: (e.target as HTMLInputElement).checked,
                  })
                }
                title={
                  row.enabled
                    ? t("promotions.coins.action_disable")
                    : t("promotions.coins.action_enable")
                }
                aria-label={
                  row.enabled
                    ? t("promotions.coins.action_disable")
                    : t("promotions.coins.action_enable")
                }
                class="toggle toggle-primary toggle-sm"
              />
              {props.canManage !== false && (
                <button
                  type="button"
                  onClick={() => handleEdit(row)}
                  class="btn btn-xs btn-ghost hover:bg-base-content/10"
                  title={t("promotions.coins.action_edit")}
                  aria-label={t("promotions.coins.action_edit")}
                >
                  <span class="material-symbols-outlined text-base">edit</span>
                </button>
              )}
            </div>
          ),
        },
      ]);

      const renderBody = () => {
        if (programsQuery.isError.value) {
          return (
            <div class="flex flex-col items-center justify-center py-16 gap-3 text-error">
              <span class="material-symbols-outlined text-4xl">
                error_outline
              </span>
              <p class="text-sm">{t("promotions.coins.load_failed")}</p>
              <button
                class="btn btn-sm btn-outline btn-error"
                onClick={() => programsQuery.refetch()}
              >
                {t("promotions.coins.retry")}
              </button>
            </div>
          );
        }

        return (
          <ResponsiveTable
            {...{
              columns: columns.value,
              rows: programs.value,
              rowKey: (row: CoinProgramWithStats) => row.id,
              onRowClick: (row: CoinProgramWithStats) => {
                if (props.canManage !== false) handleEdit(row);
              },
              loading: programsQuery.isLoading.value,
              emptyMessage: t("promotions.coins.empty"),
            }}
          />
        );
      };

      return () => (
        <AppSubpage>
          <AppSubpageHeader
            icon="toll"
            title={
              props.scope === "group"
                ? t("group.loyalty.coins_title")
                : t("promotions.coins.page_title")
            }
            description={
              props.scope === "group"
                ? t("group.loyalty.coins_description")
                : t("promotions.coins.page_description")
            }
            v-slots={{
              actions: () =>
                props.canManage !== false ? (
                  <button
                    type="button"
                    class="btn btn-primary btn-sm gap-2 shadow-lg hover:shadow-primary/25"
                    onClick={handleNew}
                  >
                    <span class="material-symbols-outlined text-lg">add</span>
                    {t("promotions.coins.new_program")}
                  </button>
                ) : null,
            }}
          />

          <AppSubpageContent>
            <div class="space-y-5 p-4">
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard
                  icon="toll"
                  label={t("promotions.coins.stat_programs")}
                  value={programs.value.length}
                  tone="primary"
                />
                <StatCard
                  icon="check_circle"
                  label={t("promotions.coins.stat_active")}
                  value={programs.value.filter((p) => p.enabled).length}
                  tone="success"
                />
                <StatCard
                  icon="account_balance_wallet"
                  label={t("promotions.coins.stat_outstanding")}
                  value={programs.value
                    .reduce((sum, p) => sum + p.coins_outstanding, 0)
                    .toLocaleString()}
                  tone="info"
                />
              </div>

              {renderBody()}
            </div>
          </AppSubpageContent>

          {showEditor.value && props.canManage !== false && (
            <CoinProgramEditor
              {...{
                program: editingProgram.value ?? undefined,
                onClose: closeEditor,
                onSaved: closeEditor,
                scope: props.scope,
              }}
            />
          )}
        </AppSubpage>
      );
    },
  });
